import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  TreeList,
  TreeListExpandChangeEvent,
  TreeListRowClickEvent,
  TreeListSortChangeEvent,
  extendDataItem,
  mapTree,
} from '@progress/kendo-react-treelist';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserCompaniesIcon from 'src/assets/icons/UserCompaniesIcon';
import useUserCompaniesColumns from 'src/assets/static/work_area/useUserCompaniesColumns';
import LoadingPanel from 'src/components/LoadingPanel';
import { DropDownCategories } from 'src/types/generic';
import { UserTreeNode } from 'src/types/work_area';
import { useContextSelector } from 'use-context-selector';
import { useWorkAreaCompanyUsersTree } from './useWorkArea';
import { useWorkAreaFilterOptions } from './useWorkAreaFilterOptions';
import { WorkAreaContext } from './WorkAreaContext';
import { findUserRowByClientId, scrollWorkAreaPanelToSelector } from './workAreaImpersonation';
import {
  workAreaFilterDropdownClassName,
  workAreaPanelActionsClassName,
  workAreaPanelBodyClassName,
  workAreaPanelHeaderClassName,
  workAreaPanelTitleClassName,
  workAreaUsersPanelClassName,
} from './workAreaPanelLayout';

interface UserCompany {
  id: string;
  realClientId: number;
  userNameAndRole: string;
  spendingLimit: string;
  title: string;
  role: string;
  contact: string;
  emailAddress: string;
  loginName?: string;
  disabled?: boolean;
  subUsers?: UserCompany[];
}

const SUB_ITEMS_FIELD = 'subUsers';
const EXPAND_FIELD = 'expanded';
const DATA_ITEM_KEY = 'id';
const mapUserNodeToCompany = (
  node: UserTreeNode,
  parentRowId: string,
  siblingIndex: number
): UserCompany => {
  const id =
    parentRowId === ''
      ? `i${siblingIndex}c${node.clientId}`
      : `${parentRowId}/i${siblingIndex}c${node.clientId}`;
  return {
    id,
    realClientId: node.clientId,
    userNameAndRole: node.contact ?? '',
    spendingLimit: node.poSpendingLimit ?? '',
    title: '',
    role: '',
    contact: node.phoneNumber ?? '',
    emailAddress: node.contactEmail ?? node.email ?? '',
    loginName: node.loginName ?? '',
    disabled: node.isDeleted,
    subUsers: node.children?.map((child, i) => mapUserNodeToCompany(child, id, i)) ?? [],
  };
};

const UserCompanies = () => {
  const { t } = useTranslation();
  const userCompaniesFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.userCompaniesFilter
  );
  const setUserCompaniesFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.setUserCompaniesFilter
  );
  const selectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyId
  );
  const selectedCompanyName = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyName
  );
  const isImpersonating = useContextSelector(WorkAreaContext, (context) => context.isImpersonating);
  const impersonatedClientId = useContextSelector(
    WorkAreaContext,
    (context) => context.impersonatedClientId
  );
  const sessionClientId = useContextSelector(WorkAreaContext, (context) => context.sessionClientId);
  const isImpersonatedClient = useContextSelector(
    WorkAreaContext,
    (context) => context.isImpersonatedClient
  );

  const [expandedUserIds, setExpandedUserIds] = useState<string[]>([]);
  const userListScrollRef = useRef<HTMLDivElement>(null);
  const hasAppliedImpersonationUserExpandRef = useRef(false);
  const hasScrolledToSessionUserRef = useRef(false);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'userNameAndRole', dir: 'asc' }]);

  const { data: userTreeResponse, isLoading: isUserTreeLoading } = useWorkAreaCompanyUsersTree();

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const value = e.value as DropDownCategories;
      setUserCompaniesFilter(value);
    },
    [setUserCompaniesFilter]
  );

  const onSortChange = useCallback((event: TreeListSortChangeEvent) => {
    setSort(event.sort);
  }, []);

  const rootNode = userTreeResponse?.nodes?.[0];
  const companyName = useMemo(
    () => (selectedCompanyId ? rootNode?.companyName ?? selectedCompanyName ?? '' : ''),
    [selectedCompanyId, rootNode?.companyName, selectedCompanyName]
  );
  const usersPanelTitle = useMemo(
    () => (companyName ? `${t('users')} - ${companyName}` : t('users')),
    [companyName, t]
  );
  const selectedUsers = useMemo(() => {
    if (selectedCompanyId === null || !rootNode) {
      return [];
    }
    return [mapUserNodeToCompany(rootNode, '', 0)];
  }, [selectedCompanyId, rootNode]);
  const descendantsById = useMemo(() => {
    const descendantsMap = new Map<string, string[]>();
    const walk = (users: UserCompany[]) => {
      users.forEach((user) => {
        if (user.subUsers?.length) {
          descendantsMap.set(
            user.id,
            user.subUsers.map((child) => child.id)
          );
          walk(user.subUsers);
        }
      });
    };
    walk(selectedUsers);
    const collectAllDescendants = (id: string): string[] => {
      const directChildren = descendantsMap.get(id) || [];
      const nestedChildren = directChildren.flatMap((childId) => collectAllDescendants(childId));
      return [...directChildren, ...nestedChildren];
    };
    const allDescendantsMap = new Map<string, string[]>();
    descendantsMap.forEach((_, id) => {
      allDescendantsMap.set(id, collectAllDescendants(id));
    });
    return allDescendantsMap;
  }, [selectedUsers]);

  const onExpandChange = useCallback(
    (event: TreeListExpandChangeEvent) => {
      const targetId = String(event.dataItem.id);
      setExpandedUserIds((prev) => {
        const descendantIds = descendantsById.get(targetId) || [];
        const shouldCollapse = prev.includes(targetId);
        if (!shouldCollapse) {
          const next = new Set([...prev, targetId, ...descendantIds]);
          return Array.from(next);
        }

        const next = prev.filter((id) => id !== targetId && !descendantIds.includes(id));
        return next;
      });
    },
    [descendantsById]
  );
  const onRowClick = useCallback(
    (event: TreeListRowClickEvent) => {
      const target = event.syntheticEvent?.target as HTMLElement | null;
      if (
        target?.closest(
          'button,a,input,select,textarea,[role="button"],.k-icon,.k-svg-icon,.k-i-expand,.k-i-collapse'
        )
      ) {
        return;
      }

      const targetId = event.dataItem?.id;
      if (!targetId || !descendantsById.has(targetId)) {
        return;
      }

      setExpandedUserIds((prev) => {
        const descendantIds = descendantsById.get(targetId) || [];
        const shouldCollapse = prev.includes(targetId);
        if (!shouldCollapse) {
          return Array.from(new Set([...prev, targetId, ...descendantIds]));
        }
        return prev.filter((id) => id !== targetId && !descendantIds.includes(id));
      });
    },
    [descendantsById]
  );

  const columns = useUserCompaniesColumns();
  const localizedFilterOptions = useWorkAreaFilterOptions();
  const selectedFilterOption = useMemo(
    () =>
      localizedFilterOptions.find((option) => option.value === userCompaniesFilter?.value) ||
      localizedFilterOptions[0],
    [userCompaniesFilter?.value, localizedFilterOptions]
  );
  const cellRender = useCallback(
    (
      defaultRendering: React.ReactElement | null,
      { dataItem, field }: { dataItem?: UserCompany; field?: string }
    ) => {
      if (!defaultRendering) return defaultRendering;
      if (field !== 'userNameAndRole' || !dataItem?.disabled) return defaultRendering;
      return React.cloneElement(
        defaultRendering,
        defaultRendering.props,
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">{defaultRendering.props.children}</div>
          <span className="rounded bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
            {t('disabled')}
          </span>
        </div>
      );
    },
    [t]
  );
  const sortTreeData = useCallback(
    (data: UserCompany[], sortDescriptors: SortDescriptor[]): UserCompany[] => {
      if (!sortDescriptors.length) {
        return data;
      }
      const sorted = orderBy(data, sortDescriptors);
      return sorted.map((item) =>
        item.subUsers?.length
          ? { ...item, subUsers: sortTreeData(item.subUsers, sortDescriptors) }
          : item
      );
    },
    []
  );

  const processedUsers = useMemo(() => {
    const sorted = sortTreeData(selectedUsers, sort);
    return mapTree(sorted, SUB_ITEMS_FIELD, (item) =>
      extendDataItem(item, SUB_ITEMS_FIELD, {
        [EXPAND_FIELD]: expandedUserIds.includes(item.id),
      })
    );
  }, [expandedUserIds, selectedUsers, sort, sortTreeData]);
  const showGrid = selectedCompanyId !== null;

  const allExpandableUserIds = useMemo(() => Array.from(descendantsById.keys()), [descendantsById]);

  useEffect(() => {
    if (selectedCompanyId === null) {
      setExpandedUserIds([]);
      hasAppliedImpersonationUserExpandRef.current = false;
      hasScrolledToSessionUserRef.current = false;
      return;
    }
    setExpandedUserIds(allExpandableUserIds);
  }, [selectedCompanyId, allExpandableUserIds]);

  useEffect(() => {
    if (
      !isImpersonating ||
      impersonatedClientId == null ||
      selectedCompanyId === null ||
      isUserTreeLoading ||
      !selectedUsers.length ||
      hasAppliedImpersonationUserExpandRef.current
    ) {
      return;
    }

    const lookup = findUserRowByClientId(selectedUsers, impersonatedClientId);
    if (!lookup) {
      return;
    }

    hasAppliedImpersonationUserExpandRef.current = true;
    const descendantIds = descendantsById.get(lookup.rowId) || [];
    const expandIds = [...lookup.ancestorRowIds, lookup.rowId, ...descendantIds];
    setExpandedUserIds((prev) => Array.from(new Set([...prev, ...expandIds])));
  }, [
    descendantsById,
    impersonatedClientId,
    isImpersonating,
    isUserTreeLoading,
    selectedCompanyId,
    selectedUsers,
  ]);

  // Scroll after the session user row is painted (logged-in self or impersonated).
  useEffect(() => {
    if (
      sessionClientId == null ||
      selectedCompanyId === null ||
      isUserTreeLoading ||
      !selectedUsers.length ||
      hasScrolledToSessionUserRef.current
    ) {
      return;
    }

    if (
      !scrollWorkAreaPanelToSelector(userListScrollRef.current, '.user-companies-row-impersonated')
    ) {
      return;
    }

    hasScrolledToSessionUserRef.current = true;
  }, [isUserTreeLoading, processedUsers, selectedCompanyId, selectedUsers, sessionClientId]);

  const rowRender = useCallback(
    (tr: React.ReactElement, props: { dataItem?: UserCompany }) => {
      const { dataItem } = props;
      const className = [
        tr.props.className,
        dataItem && isImpersonatedClient(dataItem.realClientId)
          ? 'user-companies-row-impersonated'
          : '',
      ]
        .filter(Boolean)
        .join(' ');
      return React.cloneElement(tr, { className });
    },
    [isImpersonatedClient]
  );

  return (
    <div className={workAreaUsersPanelClassName}>
      <div className={workAreaPanelHeaderClassName}>
        <h2 className={workAreaPanelTitleClassName} title={usersPanelTitle}>
          {usersPanelTitle}
        </h2>
        <div className={workAreaPanelActionsClassName}>
          <DropDownList
            id="catalog-category-dropdown"
            className={workAreaFilterDropdownClassName}
            data={localizedFilterOptions}
            textField="label"
            dataItemKey="value"
            value={selectedFilterOption}
            onChange={handleFilterChange}
          />
        </div>
      </div>
      <div ref={userListScrollRef} className={`${workAreaPanelBodyClassName} user-companies-list`}>
        {showGrid && isUserTreeLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingPanel />
          </div>
        )}
        {showGrid ? (
          <TreeList
            className="!h-full"
            style={{ height: '100%' }}
            expandField={EXPAND_FIELD}
            subItemsField={SUB_ITEMS_FIELD}
            onExpandChange={onExpandChange}
            onRowClick={onRowClick}
            data={processedUsers}
            columns={columns}
            dataItemKey={DATA_ITEM_KEY}
            sortable={true}
            resizable={false}
            sort={sort}
            onSortChange={onSortChange}
            cellRender={cellRender}
            rowRender={rowRender}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <UserCompaniesIcon />
            <div className="text-base font-medium text-gray-700 mt-4">
              {t('selectCompanyToViewUsers')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCompanies;
